import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  ModalProps,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { useGetCoinsByTypeQuery } from '../../../../api';
import { Option } from '../../../../enums';
import { PostProviderFeeDTO } from '../../../../api/controllers/providers';

type CreateFeeGroupModalProps = {
  open: boolean;
  onClose: () => void;
  onCreate: () => void;
  fields: PostProviderFeeDTO['body'] | null;
  fieldsHandler: (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  isLoading: boolean;
};

export const CreateProviderFeeModal: FC<CreateFeeGroupModalProps> = (props) => {
  const { open, onClose, onCreate, fields, fieldsHandler, isLoading } = props;
  const { t } = useTranslation();

  // api
  const { data: coinList, isLoading: isCoinsLoading } = useGetCoinsByTypeQuery({ type: 'crypto' });

  // validation for required fields
  const requiredFields = [
    'coin_id',
    'fixed_amount',
    'percent',
    'option',
  ] as (keyof PostProviderFeeDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '') {
        return true;
      }
    }

    if (Number(fields.fixed_amount) < 0 || Number(fields.percent) < 0) return true;

    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  // elements
  const coinSelectOptions = coinList?.coins.map((coin) => {
    return (
      <MenuItem key={coin.id} value={coin.id}>
        {coin.name}
      </MenuItem>
    );
  });

  const optionSelectOptions = Object.values(Option).map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {it}
      </MenuItem>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('provider-fees.modals.create.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            select
            name="coin_id"
            value={fields?.coin_id ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('provider-fees.modals.create.fields.coin')}
            fullWidth
          >
            {coinSelectOptions}
          </TextField>

          <TextField
            select
            name="option"
            value={fields?.option ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('provider-fees.modals.create.fields.option')}
            fullWidth
          >
            {optionSelectOptions}
          </TextField>

          <TextField
            name="fixed_amount"
            type="number"
            value={fields?.fixed_amount ?? ''}
            onChange={(e) => fieldsHandler(e)}
            inputProps={{ min: 0, step: '0.01' }}
            label={t('provider-fees.modals.create.fields.fixed-amount')}
            fullWidth
          />

          <TextField
            name="percent"
            type="number"
            value={fields?.percent ?? ''}
            onChange={(e) => fieldsHandler(e)}
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            label={t('provider-fees.modals.create.fields.percent')}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onCreate()}
          disabled={isCoinsLoading || isLoading || checkSubmitDisabling()}
        >
          {t('fees.modals.create.create-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onClose()}>
          {t('fees.modals.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
